/**
 * App state: the device, the unlocked session, and when to sync. One instance for the app.
 * Components subscribe and re-render on change.
 */
import { type LoginResponse, createClinicClock } from '@assist/core';
import {
  Device,
  DeviceRevokedError,
  type EnrolledUser,
  NeedsOnlineLoginError,
  type Session,
  type StoreStatus,
  type SyncReport,
  httpTransport,
  loginOnline,
} from '@assist/offline';
import type { PackSetting } from './packs.js';

export type Phase = 'loading' | 'signin' | 'setpin' | 'unlock' | 'ready' | 'revoked';

export const clock = createClinicClock();
/** Lock after this long with nobody using the app (a shared clinic device still locks itself). */
const AUTO_LOCK_MS = 30 * 60_000;
const SYNC_EVERY_MS = 2 * 60_000;
const SYNC_AFTER_SAVE_MS = 3_000;

export class Controller {
  phase: Phase = 'loading';
  device: Device | null = null;
  session: Session | null = null;
  users: EnrolledUser[] = [];
  pendingLogin: LoginResponse | null = null;
  status: StoreStatus | null = null;
  syncing = false;
  online = typeof navigator === 'undefined' ? true : navigator.onLine;
  lastReport: SyncReport | null = null;
  notice: string | null = null;
  updateReady = false;
  /** Content-pack settings of this facility (cached on the device; refreshed on each sync). */
  packSettings: PackSetting[] = [];

  private listeners = new Set<() => void>();
  private transport = httpTransport('');
  private syncTimer: ReturnType<typeof setTimeout> | undefined;
  private interval: ReturnType<typeof setInterval> | undefined;
  private idleTimer: ReturnType<typeof setTimeout> | undefined;

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }
  private emit() {
    for (const fn of this.listeners) fn();
  }

  async init() {
    this.device = await Device.open();
    this.users = await this.device.enrolledUsers();
    this.phase = this.users.length ? 'unlock' : 'signin';
    window.addEventListener('online', () => {
      this.online = true;
      this.emit();
      this.scheduleSync(500);
    });
    window.addEventListener('offline', () => {
      this.online = false;
      this.emit();
    });
    // any sign of someone using it keeps it open: touching, typing, scrolling, moving between pages
    for (const ev of ['pointerdown', 'keydown', 'wheel', 'touchmove', 'scroll', 'hashchange'])
      window.addEventListener(ev, () => this.touch(), { passive: true, capture: true });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.touch();
    });
    this.emit();
  }

  async signIn(username: string, password: string) {
    if (!this.device) return;
    this.pendingLogin = await loginOnline({ username, password, deviceId: this.device.deviceId });
    this.phase = 'setpin';
    this.emit();
  }

  async setPin(pin: string) {
    if (!this.device || !this.pendingLogin) return;
    this.session = await this.device.signInOnline(this.pendingLogin, pin);
    this.pendingLogin = null;
    await this.ready();
  }

  async unlock(userId: string, pin: string) {
    if (!this.device) return;
    this.session = await this.device.unlock(userId, pin);
    await this.ready();
  }

  private async ready() {
    this.phase = 'ready';
    this.notice = null;
    this.users = (await this.device?.enrolledUsers()) ?? [];
    await this.loadPackSettings();
    await this.refresh();
    this.interval = setInterval(() => void this.syncNow(), SYNC_EVERY_MS);
    this.touch();
    void this.syncNow();
  }

  lock() {
    this.session?.lock();
    this.session = null;
    clearInterval(this.interval);
    clearTimeout(this.syncTimer);
    clearTimeout(this.idleTimer);
    this.phase = this.users.length ? 'unlock' : 'signin';
    this.emit();
  }

  toSignIn() {
    this.phase = 'signin';
    this.emit();
  }

  private touch() {
    if (this.phase !== 'ready') return;
    clearTimeout(this.idleTimer);
    this.idleTimer = setTimeout(() => this.lock(), AUTO_LOCK_MS);
  }

  async refresh() {
    this.status = this.session ? await this.session.status() : null;
    this.emit();
  }

  /** Call after every local save: sync soon, in the background. */
  saved() {
    void this.refresh();
    this.scheduleSync(SYNC_AFTER_SAVE_MS);
  }

  scheduleSync(ms: number) {
    clearTimeout(this.syncTimer);
    this.syncTimer = setTimeout(() => void this.syncNow(), ms);
  }

  async syncNow() {
    const s = this.session;
    if (!s || this.syncing || !s.auth) return;
    this.syncing = true;
    this.emit();
    try {
      this.lastReport = await s.sync(this.transport);
      if (this.lastReport.offline) this.online = false;
      else {
        this.online = true;
        await this.fetchPackSettings();
      }
    } catch (e) {
      if (e instanceof DeviceRevokedError) {
        this.session = null;
        this.phase = 'revoked';
      } else if (e instanceof NeedsOnlineLoginError) {
        this.notice = e.message;
      } else {
        this.notice = e instanceof Error ? e.message : String(e);
      }
    } finally {
      this.syncing = false;
      await this.refresh();
    }
  }

  // ---------------------------------------------------------------- content packs

  private packKey() {
    return `packSettings:${this.session?.user.facilityId ?? ''}`;
  }

  private async loadPackSettings() {
    const row = await this.device?.db.meta.get(this.packKey());
    this.packSettings = (row?.v as PackSetting[] | undefined) ?? [];
  }

  async api<T>(path: string, init: RequestInit = {}): Promise<T> {
    const token = this.session?.auth?.token;
    if (!token) throw new Error('Sign in with your password to change settings');
    const res = await fetch(path, {
      ...init,
      headers: {
        authorization: `Bearer ${token}`,
        ...(init.body ? { 'content-type': 'application/json' } : {}),
      },
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      throw new Error(body.error ?? `Server answered ${res.status}`);
    }
    return (await res.json()) as T;
  }

  /** Fetch this facility's pack settings; keep the cached ones if the server cannot be reached. */
  async fetchPackSettings() {
    try {
      const r = await this.api<{ packs: PackSetting[] }>('/api/facility/packs');
      this.packSettings = r.packs;
      await this.device?.db.meta.put({ k: this.packKey(), v: r.packs });
      this.emit();
    } catch {
      // Offline or signed out: the cached settings stay in force.
    }
  }

  /** Admin only, online only. */
  async setPack(packId: string, enabled: boolean, allowDraft: boolean) {
    await this.api(`/api/admin/packs/${encodeURIComponent(packId)}`, {
      method: 'PUT',
      body: JSON.stringify({ enabled, allowDraft }),
    });
    await this.fetchPackSettings();
  }
}

export const app = new Controller();
